import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentContainer from '../../components/common/ContentContainer/ContentContainer';
import { Header } from '../../components/common/Header';
import { TextArea } from '../../components/common/TextArea/TextArea';
import Button from '../../components/common/Button/Button';
import { useExerciseGenerator } from '../../app/hooks/useExerciseGenerator';
import { useSnackbar } from '../../navigation/snackbar';
import styles from './ExerciseGeneratorPage.module.css';

type Step = 'description' | 'prompt' | 'json';

export const ExerciseGeneratorPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>('description');
  const [description, setDescription] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const { generatePrompt, parseExerciseJSON, error } = useExerciseGenerator();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleGeneratePrompt = () => {
    if (!description.trim()) {
      showSnackbar('Please enter an exercise description', 'error');
      return;
    }

    const prompt = generatePrompt(description);
    setGeneratedPrompt(prompt);
    setCurrentStep('prompt');
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      showSnackbar('Prompt copied to clipboard!', 'success');
    } catch {
      showSnackbar('Failed to copy prompt', 'error');
    }
  };

  const handleParseJSON = () => {
    if (!jsonInput.trim()) {
      showSnackbar('Please paste the generated JSON', 'error');
      return;
    }

    const exercise = parseExerciseJSON(jsonInput);

    if (exercise) {
      showSnackbar('Exercise parsed successfully!', 'success');
      navigate('/opdrachten/nieuw', { state: { generatedExercise: exercise } });
    } else if (error) {
      showSnackbar(`Parsing failed: ${error}`, 'error');
    }
  };

  const handleBack = () => {
    if (currentStep === 'prompt') {
      setCurrentStep('description');
    } else if (currentStep === 'json') {
      setCurrentStep('prompt');
    }
  };

  const getStepClass = (step: Step, currentStep: Step, nextStep?: Step) => {
    const baseClass = styles.stepIndicator;
    if (currentStep === step) return `${baseClass} ${styles.active}`;
    if (nextStep && currentStep === nextStep) return `${baseClass} ${styles.completed}`;
    return baseClass;
  };

  return (
    <ContentContainer>
      <div className={styles.generatorPage}>
        <Header size={2} content="Exercise Generator" />

        <div className={styles.steps}>
          <div className={getStepClass('description', currentStep, 'prompt')}>1. Description</div>
          <div className={getStepClass('prompt', currentStep, 'json')}>2. Copy Prompt</div>
          <div className={getStepClass('json', currentStep)}>3. Paste JSON</div>
        </div>

        <div className={styles.content}>
          {currentStep === 'description' && (
            <>
              <p className={styles.description}>Describe the exercise you want to generate. Your description should always include:</p>
              <ul className={styles.requirements}>
                <li>
                  <strong>Platform:</strong> SCRATCH, MICROBIT, or COSPACES
                </li>
                <li>
                  <strong>Age range:</strong> Target age group for the exercise
                </li>
                <li>
                  <strong>Exercise details:</strong> What should students build or learn
                </li>
              </ul>

              <div className={styles.example}>
                <strong>Example:</strong> &quot;Generate a Scratch exercise for kids between age 8 and 12. It should be a racing game where multiple
                players can race each other.&quot;
              </div>

              <div className={styles.inputSection}>
                <TextArea
                  label="Exercise Description"
                  value={description}
                  onChange={setDescription}
                  placeholder="Describe your exercise..."
                  rows={8}
                />
              </div>

              <div className={styles.actions}>
                <Button type="primary" onClick={handleGeneratePrompt}>
                  Generate Prompt
                </Button>
              </div>
            </>
          )}

          {currentStep === 'prompt' && (
            <>
              <p className={styles.description}>Copy this prompt and paste it into any AI assistant (Claude, ChatGPT, etc.):</p>

              <div className={styles.promptContainer}>
                <TextArea label="AI Prompt" value={generatedPrompt} onChange={() => {}} rows={20} disabled />
              </div>

              <div className={styles.actions}>
                <Button type="secondary" onClick={handleBack}>
                  Back
                </Button>
                <Button type="primary" onClick={handleCopyPrompt}>
                  Copy Prompt
                </Button>
                <Button type="primary" onClick={() => setCurrentStep('json')}>
                  Next: Paste JSON
                </Button>
              </div>
            </>
          )}

          {currentStep === 'json' && (
            <>
              <p className={styles.description}>Paste the JSON response from the AI here:</p>

              <div className={styles.inputSection}>
                <TextArea label="Generated JSON" value={jsonInput} onChange={setJsonInput} placeholder="Paste JSON here..." rows={20} />
              </div>

              <div className={styles.actions}>
                <Button type="secondary" onClick={handleBack}>
                  Back
                </Button>
                <Button type="primary" onClick={handleParseJSON}>
                  Create Exercise
                </Button>
              </div>

              {error && <div className={styles.error}>Error: {error}</div>}
            </>
          )}
        </div>
      </div>
    </ContentContainer>
  );
};
